import { CanvasTexture, RepeatWrapping, SRGBColorSpace } from 'three';
import { MONOGRAM_PATHS, MONOGRAM_VIEWBOX } from 'components/Monogram/paths';

/* Cloth, generated once and shared by every book.

   This is the thing CSS could not reach. The stack faked cloth with crossed
   gradients over a flat surface, which reads as a pattern printed on card. A
   bump map instead perturbs the surface normal, so the weave is lit -- it
   catches the key light on one side of each thread and shades on the other,
   and it changes as the book turns. Stripe does the same and shares one map
   across their whole catalogue (`shared_glitter` in their material JSON).

   Generated rather than shipped: it is noise, and a few lines of canvas beat
   another asset to download. */

let weaveCache = null;
let clothCache = null;
let glitterCache = null;

/**
 * A square of book cloth, as a canvas.
 *
 * Bookbinding linen is an over-under weave of threads whose thickness varies
 * along their length -- the slubs are most of why it reads as cloth. Drawing
 * it as alternating hard-edged bars gives a screen door: a perfectly regular
 * grid that tessellates visibly however small you make it.
 *
 * So: a real interlace, per-thread amplitude so no two threads match, a little
 * wander in the thread positions, and per-pixel grain over the top. Generated
 * once at a size big enough that the repeat is not the thing your eye finds.
 */
function weaveCanvas(size = 1024) {
  if (weaveCache) return weaveCache;

  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  const image = ctx.createImageData(size, size);
  const { data } = image;

  const pitch = 4;
  const threads = Math.ceil(size / pitch) + 1;
  /* Per-thread thickness. This is the slub, and it is what makes it organic. */
  const warpAmp = new Float32Array(threads);
  const weftAmp = new Float32Array(threads);
  const warpOff = new Float32Array(threads);
  const weftOff = new Float32Array(threads);
  for (let i = 0; i < threads; i++) {
    warpAmp[i] = 0.62 + Math.random() * 0.5;
    weftAmp[i] = 0.62 + Math.random() * 0.5;
    /* A little wander, so the grid is not perfectly ruled. */
    warpOff[i] = (Math.random() - 0.5) * 0.9;
    weftOff[i] = (Math.random() - 0.5) * 0.9;
  }

  for (let y = 0; y < size; y++) {
    const hi = Math.floor(y / pitch);
    const fy = (y - hi * pitch + weftOff[hi]) / pitch;
    const weft = Math.sin(Math.max(0, Math.min(1, fy)) * Math.PI) * weftAmp[hi];

    for (let x = 0; x < size; x++) {
      const wi = Math.floor(x / pitch);
      const fx = (x - wi * pitch + warpOff[wi]) / pitch;
      const warp = Math.sin(Math.max(0, Math.min(1, fx)) * Math.PI) * warpAmp[wi];

      /* The interlace: alternate which thread passes over at each crossing. */
      const over = (wi + hi) % 2 === 0;
      const v = over ? warp * 0.68 + weft * 0.32 : weft * 0.68 + warp * 0.32;

      const grain = (Math.random() - 0.5) * 26;
      const grey = Math.max(0, Math.min(255, 128 + (v - 0.62) * 96 + grain));

      const i = (y * size + x) * 4;
      data[i] = data[i + 1] = data[i + 2] = grey;
      data[i + 3] = 255;
    }
  }

  ctx.putImageData(image, 0, 0);
  weaveCache = canvas;
  return canvas;
}

/**
 * The same cloth as a bump map. Perturbing the normal is what makes the weave
 * catch the key light on one side of each thread and shade on the other.
 */
export function clothBumpTexture() {
  if (clothCache) return clothCache;
  const texture = new CanvasTexture(weaveCanvas());
  texture.wrapS = texture.wrapT = RepeatWrapping;
  clothCache = texture;
  return texture;
}

/**
 * Specular breakup. Cloth does not reflect evenly -- the highlight should
 * scatter across the weave rather than sit as one clean sheen.
 */
export function glitterTexture(size = 256) {
  if (glitterCache) return glitterCache;

  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  const image = ctx.createImageData(size, size);
  const { data } = image;

  for (let i = 0; i < data.length; i += 4) {
    const v = 90 + Math.random() * 165;
    data[i] = data[i + 1] = data[i + 2] = v;
    data[i + 3] = 255;
  }

  ctx.putImageData(image, 0, 0);

  const texture = new CanvasTexture(canvas);
  texture.wrapS = texture.wrapT = RepeatWrapping;
  glitterCache = texture;
  return texture;
}

/**
 * The page block: one texture for all three cut edges.
 *
 * The leaves of a book are stacked along its THICKNESS, so every cut edge
 * shows the same thing -- a run of leaf edges as lines perpendicular to the
 * thickness, whichever face you look at. One texture serves all three, tiled
 * along the thickness and stretched along the other axis, where it is uniform
 * anyway.
 *
 * Cream rather than white, and not evenly ruled: a real block has the odd
 * leaf standing slightly proud, and that irregularity is what stops it reading
 * as corduroy.
 */
export function pagesTexture(width = 8, height = 64) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  for (let y = 0; y < height; y++) {
    /* Leaf, shadow between leaves, and every so often a deeper gap where a
       signature falls. */
    const cycle = y % 3;
    const t = cycle === 0 ? 0.74 : cycle === 1 ? 0.95 : 1;
    const gather = y % 7 === 0 ? 0.82 : 1;
    const jitter = 0.965 + Math.random() * 0.035;
    const v = Math.round(240 * t * gather * jitter);
    ctx.fillStyle = `rgb(${v}, ${Math.round(v * 0.975)}, ${Math.round(v * 0.915)})`;
    ctx.fillRect(0, y, width, 1);
  }

  const texture = new CanvasTexture(canvas);
  texture.wrapS = texture.wrapT = RepeatWrapping;
  return texture;
}

export const DEFAULT_FONT = 'Gotham, Helvetica, Arial, sans-serif';

/* The faces a book may ask for by name in its manifest. Kept to a list rather
   than taking an arbitrary family string: canvas resolves a font at draw time
   and falls back silently, so an unavailable name would be stamped in whatever
   happened to be loaded with nothing to say it had happened. */
export const SPINE_FONTS = {
  sans: DEFAULT_FONT,
  serif: 'Georgia, "Times New Roman", serif',
  slab: '"Rockwell", "Courier New", Georgia, serif',
  mono: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
};

/**
 * Whether the site's own face is available to canvas yet.
 *
 * A canvas font string is resolved at draw time and silently falls back -- it
 * will not wait and it will not tell you. So a texture drawn before the woff2
 * lands is stamped in Helvetica for the life of the page, which is what every
 * spine here has quietly been.
 */
export function spineFontReady() {
  if (typeof document === 'undefined' || !document.fonts) return Promise.resolve();
  return Promise.all(
    Object.values(SPINE_FONTS).map(face => document.fonts.load(`500 100px ${face}`))
  ).then(() => document.fonts.ready);
}

/**
 * The spine: accent ground with the title and author set across it.
 *
 * Rendered to a canvas because there is no text in a WebGL scene otherwise --
 * the alternative is a glyph mesh, which is far more machinery than a label
 * needs. Drawn at the face's own aspect so nothing is stretched when mapped on.
 *
 * The canvas's long axis is the book's length, so standing up, x=0 is the head
 * of the spine and the short axis runs across its thickness.
 */
export function spineTexture(
  book,
  { accent, ink, ratio, font, authorLines, cloth = 1 }
) {
  const width = 2048;
  const height = Math.max(64, Math.round(width * ratio));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  /* The foil mask: black everywhere, white exactly where the lettering is.
     Stripe authors one of these per book because they know where the real foil
     was stamped. We are drawing the type ourselves, so we get it for nothing --
     draw each glyph to both canvases and the mask is always in register. */
  const foil = document.createElement('canvas');
  foil.width = width;
  foil.height = height;
  const fctx = foil.getContext('2d');
  fctx.fillStyle = '#000';
  fctx.fillRect(0, 0, width, height);

  ctx.fillStyle = accent;
  ctx.fillRect(0, 0, width, height);

  /* The same cloth the bump uses, composited into the diffuse. The bump only
     shows where the light catches it; binding cloth reads as cloth from any
     angle, and this is what stops the spine looking like a printed rectangle.
     Overlay rather than a flat draw, so it modulates the accent instead of
     greying it out. */
  /* How much linen this particular book wears. A printed paper case and a
     bound cloth one are different objects, and one weave strength flattered
     some of the covers and fought others. */
  const pattern = cloth > 0 ? ctx.createPattern(weaveCanvas(), 'repeat') : null;
  if (pattern) {
    ctx.save();
    ctx.globalAlpha = 0.32 * cloth;
    ctx.globalCompositeOperation = 'overlay';
    ctx.fillStyle = pattern;
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
  }

  /* A spine is ROUND, and that is most of what sells it. The two long edges
     turn away from you into the hinges and go dark; the crown catches the key
     light. Painted rather than modelled -- rounding the geometry would mean
     abandoning the box, and at these angles the shading carries it.

     Off-centre, because the key light is up and to the left. A highlight
     sitting dead centre reads as a printed stripe, not as a curved surface. */
  const round = ctx.createLinearGradient(0, 0, 0, height);
  round.addColorStop(0, 'rgba(0,0,0,0.5)');
  round.addColorStop(0.06, 'rgba(0,0,0,0.16)');
  round.addColorStop(0.3, 'rgba(255,255,255,0.16)');
  round.addColorStop(0.62, 'rgba(255,255,255,0.02)');
  round.addColorStop(0.94, 'rgba(0,0,0,0.2)');
  round.addColorStop(1, 'rgba(0,0,0,0.5)');
  ctx.fillStyle = round;
  ctx.fillRect(0, 0, width, height);

  /* Head and tail: a case-bound book turns in slightly at each end, so the
     cloth there falls away from the light.

     It also wears a headband -- the scrap of striped silk glued over the block
     at each end. `cap` doubles as the margin the type and the mark keep clear
     of the ends. */
  const cap = height * 0.3;
  for (const from of [0, width]) {
    const dir = from === 0 ? 1 : -1;
    const reach = cap * 4;
    const shade = ctx.createLinearGradient(from, 0, from + dir * reach, 0);
    shade.addColorStop(0, 'rgba(0,0,0,0.3)');
    shade.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = shade;
    ctx.fillRect(Math.min(from, from + dir * reach), 0, reach, height);

    /* Faint on purpose: drawn at any strength it stops being a detail and
       becomes corrugation. */
    ctx.save();
    ctx.globalAlpha = 0.22;
    const bands = 5;
    for (let i = 0; i < bands; i++) {
      ctx.fillStyle = i % 2 ? '#efe6d2' : '#8d7f68';
      const bw = cap / bands;
      ctx.fillRect(from + (dir > 0 ? i * bw : -(i + 1) * bw), 0, bw, height);
    }
    ctx.restore();
  }

  const size = Math.round(height * 0.2);
  const y = height / 2;
  const pad = width * 0.055;
  ctx.textBaseline = 'middle';
  fctx.textBaseline = 'middle';
  /* Tracked open a touch. Stamped type on a narrow spine is always letterspaced
     -- set solid it closes up and turns into a smear at any distance. */
  if ('letterSpacing' in ctx) {
    ctx.letterSpacing = `${Math.round(size * 0.04)}px`;
    fctx.letterSpacing = ctx.letterSpacing;
  }

  const family = font || DEFAULT_FONT;
  const face = (px, weight) => `${weight} ${px}px ${family}`;

  const write = (text, x, align, foilStrength, offset = 0) => {
    ctx.textAlign = align;
    fctx.textAlign = align;
    ctx.fillStyle = ink;
    ctx.fillText(text, x, y + offset);
    fctx.fillStyle = `rgb(${foilStrength},${foilStrength},${foilStrength})`;
    fctx.fillText(text, x, y + offset);
  };

  /* Author first, title centred -- the order the reference uses. Standing up,
     "first" is the top of the spine.

     Each gets its own zone and is shrunk to fit it. Sharing one budget let a
     long title, centred, run back under a long author: "Martin Kleppmann
     Designing Data-Intensive Applications" collided into one line. The author
     occupies the left quarter and the centred title the middle two fifths, so
     they cannot meet whatever their lengths. */
  const fit = (text, maxFraction, weight) => {
    let px = size;
    ctx.font = face(px, weight);
    while (px > 9 && ctx.measureText(text).width > width * maxFraction) {
      px -= 1;
      ctx.font = face(px, weight);
    }
    fctx.font = ctx.font;
    return text;
  };

  /* The title owns the middle, and the author gets whatever is left between
     the head and it. Budgeted rather than guessed: a fixed author width plus
     the headband inset ran straight under a centred long title, which is how
     "Martin Kleppmann" ended up printed over "Designing Data-Intensive
     Applications". */
  const titleHalf = 0.19;
  const authorLeft = pad + cap;
  const authorRoom = 0.5 - titleHalf - authorLeft / width - 0.02;

  /* An author may be set over two lines. A long name on a narrow spine either
     shrinks to nothing or runs into the title; broken deliberately it stays at
     a readable size, which is what a real spine does with one. */
  const lines = authorLines?.length ? authorLines : book.author ? [book.author] : [];
  const lead = size * 1.12;
  lines.forEach((line, i) => {
    const offset = (i - (lines.length - 1) / 2) * lead;
    write(fit(line, authorRoom, 400), authorLeft, 'left', 190, offset);
  });

  write(fit(book.title, titleHalf * 2, 700), width / 2, 'center', 255);

  /* The publisher's mark at the tail, which is what balances the run of type.
     The site's own monogram, stamped like the lettering -- same ink, same
     foil, so it catches the light with the title rather than sitting on top of
     the cloth as a drawing. */
  /* Sized against the spine's thickness, but turned a quarter turn against the
     type. The lettering runs top-to-bottom the way a spine's does, so anything
     drawn square to it ends up on its side; the mark has to counter-rotate to
     stand up when the book does. */
  const markH = height * 0.62;
  const markW = (markH * MONOGRAM_VIEWBOX.width) / MONOGRAM_VIEWBOX.height;
  const scale = markH / MONOGRAM_VIEWBOX.height;
  /* Sits low, nearer the tail than the type is to the head -- a publisher's
     mark is set into the foot of a spine, not centred in the space left. */
  const markX = width - pad * 0.45 - cap - markH / 2;
  for (const target of [ctx, fctx]) {
    target.save();
    target.translate(markX, y);
    target.rotate(-Math.PI / 2);
    target.translate(-markW / 2, -markH / 2);
    target.scale(scale, scale);
    /* The outlines overrun the viewBox on the right; the SVG clips them with
       its own box and so must this. */
    target.beginPath();
    target.rect(0, 0, MONOGRAM_VIEWBOX.width, MONOGRAM_VIEWBOX.height);
    target.clip();
    target.fillStyle = target === ctx ? ink : 'rgb(220,220,220)';
    for (const d of MONOGRAM_PATHS) target.fill(new Path2D(d));
    target.restore();
  }

  const map = new CanvasTexture(canvas);
  map.colorSpace = SRGBColorSpace;
  const foilMap = new CanvasTexture(foil);
  return { map, foilMap };
}

/**
 * The front board: the cover art, plus the hinge strip it needs beside it.
 *
 * A case-bound board is wider than its front artwork by the hinge -- the strip
 * the cover flexes on, with the French groove running down it. Something has
 * to fill that strip. Clamping the texture's edge column is free and seamless
 * while that column is one colour, and falls apart the moment it is not: Deep
 * Work's black display type dragged a black bar the length of the hinge.
 *
 * So the strip is the edge column AVERAGED along the book's length, in bands,
 * and painted as a gradient. It keeps the colour running down the strip the way
 * the cover does while carrying none of its detail. Nothing can streak, because
 * there is no longer any detail left to streak.
 */
export function boardTexture(image, { hinge = 0.06 } = {}) {
  const artW = image.naturalWidth;
  const artH = image.naturalHeight;
  const strip = Math.max(2, Math.round((artW * hinge) / (1 - hinge)));

  const canvas = document.createElement('canvas');
  canvas.width = artW + strip;
  canvas.height = artH;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(image, strip, 0, artW, artH);

  /* Average the artwork's edge columns myself, band by band, rather than
     letting canvas downscale them. A twenty-to-one reduction in a single
     drawImage does not box-filter in any browser worth naming -- it samples,
     which is how the black of the display type kept surviving into the strip
     as blobs. Reading the pixels is exact, and it is four columns of one
     image.

     The result is painted as a gradient rather than as bands so it ramps
     rather than steps. */
  const bands = 26;
  const sample = 4;
  const stops = [];
  try {
    const { data } = ctx.getImageData(strip, 0, sample, artH);
    for (let b = 0; b < bands; b++) {
      const from = Math.floor((b * artH) / bands);
      const to = Math.max(from + 1, Math.floor(((b + 1) * artH) / bands));
      let r = 0;
      let g = 0;
      let bl = 0;
      for (let y = from; y < to; y++) {
        for (let x = 0; x < sample; x++) {
          const i = (y * sample + x) * 4;
          r += data[i];
          g += data[i + 1];
          bl += data[i + 2];
        }
      }
      const n = (to - from) * sample;
      stops.push([(b + 0.5) / bands, `rgb(${r / n},${g / n},${bl / n})`]);
    }
  } catch {
    /* A cross-origin cover taints the canvas. Ours are same-origin. */
    stops.push([0, '#3a3a3a'], [1, '#3a3a3a']);
  }

  const ramp = ctx.createLinearGradient(0, 0, 0, artH);
  ramp.addColorStop(0, stops[0][1]);
  for (const [at, colour] of stops) ramp.addColorStop(at, colour);
  ramp.addColorStop(1, stops[stops.length - 1][1]);
  ctx.fillStyle = ramp;
  ctx.fillRect(0, 0, strip, artH);

  /* The groove is an occluded channel, so a little of it is in shadow. Kept
     light: the bump map already shades the channel, and this only has to hint
     at the occlusion. Painted heavier it stops reading as a groove and starts
     reading as a grey band stuck to the edge -- which is worse on a pale cover,
     where the contrast against the artwork is largest. It no longer has a join
     to hide either, since the averaged strip IS the edge colour. */
  const reach = strip * 1.15;
  const shade = ctx.createLinearGradient(0, 0, reach, 0);
  shade.addColorStop(0, 'rgba(0,0,0,0.1)');
  shade.addColorStop(0.42, 'rgba(0,0,0,0.2)');
  shade.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = shade;
  ctx.fillRect(0, 0, reach, artH);

  const map = new CanvasTexture(canvas);
  map.colorSpace = SRGBColorSpace;

  /* The board's relief, derived from the artwork itself.
   *
   * This is the piece that makes a printed case read as a real object rather
   * than a photograph glued to a slab, and it is what the reference does too:
   * Stripe's per-book material carries a `bumpMapCustom` next to a shared base
   * bump, at a NEGATIVE scale -- their ink sits debossed, pressed into the
   * board rather than raised off it.
   *
   * Ours is generated instead of authored: the cover's own luminance, flattened
   * hard and laid over mid grey, so dark ink sinks and light ground stands. Add
   * the paper grain and the groove and the whole board is one map.
   */
  const relief = document.createElement('canvas');
  relief.width = canvas.width;
  relief.height = canvas.height;
  const rctx = relief.getContext('2d');

  rctx.fillStyle = '#808080';
  rctx.fillRect(0, 0, relief.width, relief.height);

  rctx.save();
  rctx.globalAlpha = 0.55;
  /* Flattened almost to nothing: a cover is printed, not embossed, so what is
     wanted is the faintest sense that the ink has a thickness. */
  rctx.filter = 'grayscale(1) contrast(0.3)';
  rctx.drawImage(image, strip, 0, artW, artH);
  rctx.restore();

  const grain = rctx.getImageData(0, 0, relief.width, relief.height);
  for (let i = 0; i < grain.data.length; i += 4) {
    const n = (Math.random() - 0.5) * 12;
    grain.data[i] += n;
    grain.data[i + 1] += n;
    grain.data[i + 2] += n;
  }
  rctx.putImageData(grain, 0, 0);

  /* The French groove, in the same coordinates as the strip above. */
  const channel = rctx.createLinearGradient(0, 0, strip * 1.1, 0);
  channel.addColorStop(0, 'rgba(0,0,0,0.35)');
  channel.addColorStop(0.45, 'rgba(0,0,0,0.85)');
  channel.addColorStop(0.72, 'rgba(255,255,255,0.5)');
  channel.addColorStop(1, 'rgba(128,128,128,0)');
  rctx.fillStyle = channel;
  rctx.fillRect(0, 0, strip * 1.1, artH);

  return { map, bump: new CanvasTexture(relief) };
}

/**
 * The back board. A real one is not blank -- it carries a blurb block and the
 * publisher's mark. Generated from the same accent so a book still only needs
 * its cover supplied.
 */
export function backTexture(book, { accent }) {
  const width = 1024;
  const height = Math.round(width * 1.5);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = accent;
  ctx.fillRect(0, 0, width, height);

  /* A blurb block, set as rules rather than as lettering: at the size this is
     ever seen, real text would be noise, and rules read as a paragraph. */
  ctx.fillStyle = 'rgba(255,255,255,0.13)';
  const left = width * 0.12;
  const right = width * 0.88;
  let ty = height * 0.16;
  for (let i = 0; i < 14; i++) {
    const end = i % 5 === 4 ? left + (right - left) * 0.55 : right;
    ctx.fillRect(left, ty, end - left, Math.max(2, height * 0.004));
    ty += height * 0.028;
  }

  /* An ISBN-ish block bottom left, which is the thing your eye expects. */
  ctx.fillStyle = 'rgba(255,255,255,0.09)';
  ctx.fillRect(left, height * 0.84, width * 0.26, height * 0.08);

  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  return texture;
}

/**
 * A cover for a book with no art: its cloth, the title set in the middle, a
 * short rule, and the author beneath. Plain on purpose, like a case-bound
 * book with its jacket off, and in the spine's own typeface so it reads as
 * the same object.
 *
 * Returned as a canvas carrying naturalWidth/naturalHeight, so it goes down
 * exactly the same path as decoded artwork: the board, the hinge, and the
 * colour sampling all treat it as a cover.
 */
export function plainCover(book, { accent, font = DEFAULT_FONT }) {
  const width = 800;
  const height = 1200;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.naturalWidth = width;
  canvas.naturalHeight = height;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = accent;
  ctx.fillRect(0, 0, width, height);
  const ink = luminanceOf(accent) > 0.45 ? 'rgba(20,18,15,0.88)' : 'rgba(250,246,238,0.92)';
  ctx.fillStyle = ink;
  ctx.strokeStyle = ink;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';

  /* Wrap the title to the board, shrinking it until the longest title fits in
     four lines. */
  const wrap = (text, size, maxWidth) => {
    ctx.font = `600 ${size}px ${font}`;
    const lines = [];
    let line = '';
    for (const word of text.split(/\s+/)) {
      const next = line ? `${line} ${word}` : word;
      if (ctx.measureText(next).width > maxWidth && line) {
        lines.push(line);
        line = word;
      } else {
        line = next;
      }
    }
    if (line) lines.push(line);
    return lines;
  };
  const maxWidth = width * 0.74;
  let size = 96;
  let lines = wrap(book.title, size, maxWidth);
  while ((lines.length > 4 || lines.some(l => ctx.measureText(l).width > maxWidth)) && size > 40) {
    size -= 6;
    lines = wrap(book.title, size, maxWidth);
  }
  const lead = size * 1.12;
  const block = lead * lines.length;
  let y = height * 0.44 - block / 2 + size;
  ctx.font = `600 ${size}px ${font}`;
  for (const line of lines) {
    ctx.fillText(line, width / 2, y);
    y += lead;
  }

  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(width / 2 - 60, y + 10);
  ctx.lineTo(width / 2 + 60, y + 10);
  ctx.stroke();

  ctx.font = `400 ${Math.round(size * 0.46)}px ${font}`;
  ctx.fillText(book.author, width / 2, y + 30 + size * 0.5);

  return canvas;
}

/**
 * The cover's dominant colour, read off the decoded image.
 *
 * Sampled in the browser rather than baked into the data at build time: the
 * browser has already decoded the JPEG properly, where a build script would
 * need an image library to do the same. It also means adding a book is just
 * dropping in a cover -- the cloth, spine and back board all follow from it.
 *
 * Near-black, near-white and grey pixels are skipped. A plain mean over a
 * cover with a lot of white returns mud; what we want is the colour someone
 * would name if you asked them what colour the book is.
 */
/** Relative luminance of a #rrggbb string, for deciding how type is stamped. */
export function luminanceOf(hex) {
  const n = parseInt(hex.replace('#', ''), 16);
  return luminance({ r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 });
}

const luminance = ({ r, g, b }) => {
  const f = v => {
    const n = v / 255;
    return n <= 0.03928 ? n / 12.92 : Math.pow((n + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};

const contrast = (a, b) => {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

const hex = ({ r, g, b }) =>
  `#${[r, g, b].map(n => Math.round(n).toString(16).padStart(2, '0')).join('')}`;

/**
 * The cover's palette: the colour someone would name if you asked what colour
 * the book is, plus a second one to set type in.
 *
 * Sampled in the browser rather than baked into the data at build time: the
 * browser has already decoded the JPEG, where a build script would need an
 * image library to do the same. It also means adding a book is just dropping
 * in a cover -- cloth, spine, lettering and back board all follow from it.
 *
 * Near-black, near-white and grey pixels are skipped when looking for the
 * ground colour. A plain mean over a cover with a lot of white returns mud.
 */
export function coverPalette(image, fallback = '#333333') {
  const size = 48;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });

  try {
    ctx.drawImage(image, 0, 0, size, size);
    const { data } = ctx.getImageData(0, 0, size, size);
    const buckets = new Map();

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const key = `${r >> 4},${g >> 4},${b >> 4}`;
      const hit = buckets.get(key);
      if (hit) hit.n++;
      else buckets.set(key, { r, g, b, n: 1 });
    }

    const all = [...buckets.values()].sort((x, z) => z.n - x.n);
    const isGround = c => {
      const max = Math.max(c.r, c.g, c.b);
      const min = Math.min(c.r, c.g, c.b);
      return max >= 36 && min <= 232 && max - min >= 24;
    };

    const ground = all.find(isGround) || all[0];
    if (!ground) return { accent: fallback, ink: '#ffffff' };

    /* The type colour is a real second colour off the cover, not white. White
       on a flat rectangle is what made these read as labels rather than as
       designed spines -- the reference sets copper on navy. Take the most
       common colour that is legible against the ground, and only fall back to
       plain light or dark if the cover genuinely has nothing. */
    const ink =
      all.find(c => c !== ground && c.n > 2 && contrast(c, ground) >= 4.5) ||
      null;

    const plain = luminance(ground) > 0.4 ? '#171410' : '#f2ece1';
    return { accent: hex(ground), ink: ink ? hex(ink) : plain };
  } catch {
    /* A cross-origin image taints the canvas and getImageData throws. Ours are
       same-origin, but a hot-linked cover would land here. */
    return { accent: fallback, ink: '#ffffff' };
  }
}

/**
 * A board's bump: the French groove, plus fine grain.
 *
 * The groove is the channel a hardback's cover flexes in, a few millimetres in
 * from the spine, with the board standing proud either side of it. It is the
 * detail that makes a case-bound book look case-bound rather than like a
 * printed slab, and it cannot come from the tiled cloth -- it happens once, at
 * a known place, so the board needs a map of its own rather than a repeat.
 *
 * Deliberately NOT the weave. This map is board-sized and cannot repeat, so
 * the threads would have to be drawn a texel wide and would alias into a
 * crosshatch over the cover art. The cloth belongs on the spine, which is the
 * only face here that is actually bare cloth.
 *
 * The canvas's long axis is the board's length; the groove runs down it, so it
 * is a band across the short axis. `hinge` is the fraction of the board width
 * given over to the strip between the spine edge and where the cover art
 * starts -- the groove lives entirely inside it, so it never crosses the art.
 *
 * `flip` puts it at the other edge. BoxGeometry builds the two boards' v axes
 * in opposite directions, so the spine is at v=0 on the front and v=1 on the
 * back; one map cannot serve both.
 */
export function boardBumpTexture({ width = 1024, height = 700, hinge = 0.06, flip = false }) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  /* Paper grain: enough to stop the board reading as a sheet of glass, far too
     fine to be seen as a pattern. */
  const image = ctx.createImageData(width, height);
  const { data } = image;
  for (let i = 0; i < data.length; i += 4) {
    const v = 128 + (Math.random() - 0.5) * 14;
    data[i] = data[i + 1] = data[i + 2] = v;
    data[i + 3] = 255;
  }
  ctx.putImageData(image, 0, 0);

  /* The channel sits INSIDE the hinge strip and stops exactly where the cover
     art begins, so it can never cut across the artwork. flipY means v=0 is the
     bottom of this canvas, and v=0 is the spine edge. */
  const strip = height * hinge;
  const half = strip * 0.4;
  const at = flip ? strip * 0.6 : height - strip * 0.6;
  const groove = ctx.createLinearGradient(0, at - half, 0, at + half);
  /* Dark is recessed, light is proud: the drop from the spine, the trough,
     then the lip the board stands up on. */
  for (const [stop, colour] of [
    [0, 'rgba(0,0,0,0.25)'],
    [0.28, 'rgba(0,0,0,0.9)'],
    [0.62, 'rgba(255,255,255,0.5)'],
    [1, 'rgba(0,0,0,0)'],
  ]) {
    groove.addColorStop(stop, colour);
  }
  ctx.fillStyle = groove;
  ctx.fillRect(0, at - half, width, half * 2);

  return new CanvasTexture(canvas);
}
